import React from "react";
import { Link } from "react-router-dom";
import type { ShopEntity } from "../../../../../../domain/types/company-shop";
import type { LiveConsultPluginConfig } from "../../../../../../domain/types/company-plugin";
import { Notice, Segmented, SettingsCard } from "../ui/SettingsUI";

interface OrderSettingsProps {
  shopEntityData: ShopEntity | null;
  setShopEntityData: React.Dispatch<React.SetStateAction<ShopEntity | null>>;
  /** 회사의 실시간 상담 플러그인. undefined = 불러오는 중, null = 미등록 */
  liveConsult: LiveConsultPluginConfig | null | undefined;
  companyId: number | null;
}

const CHANNEL_NAME = {
  KAKAO_CHANNEL: "카카오 채널",
  CHANNEL_TALK: "채널톡",
} as const;

/** 주문 화면 배치 그림: 가로형(옵션 | 견적), 세로형(옵션 위 · 견적 아래) */
const LayoutIcon = ({ vertical }: { vertical: boolean }) => (
  <svg width="28" height="20" viewBox="0 0 28 20" aria-hidden="true" className="inline-block mr-[0.375rem] align-[-4px]">
    <rect x="0.5" y="0.5" width="27" height="19" rx="2" fill="none" stroke="currentColor" />
    {vertical ? (
      <>
        <rect x="3" y="3" width="22" height="7" rx="1" fill="currentColor" opacity="0.35" />
        <rect x="3" y="12" width="22" height="5" rx="1" fill="currentColor" />
      </>
    ) : (
      <>
        <rect x="3" y="3" width="14" height="14" rx="1" fill="currentColor" opacity="0.35" />
        <rect x="19" y="3" width="6" height="14" rx="1" fill="currentColor" />
      </>
    )}
  </svg>
);

/** 주문 · 상담 탭: 상품 주문 화면과 상담 버튼처럼 메인 화면 밖의 쇼핑몰 동작 */
export const OrderSettings = ({ shopEntityData, setShopEntityData, liveConsult, companyId }: OrderSettingsProps) => {
  const update = (patch: Partial<ShopEntity>) => setShopEntityData((original) => (original ? { ...original, ...patch } : original));

  return (
    <>
      <SettingsCard title="상품 주문 화면" description="옵션과 견적 영역의 배치입니다. 미리보기가 상품 주문 화면으로 바뀝니다.">
        <Segmented
          ariaLabel="상품 주문 화면 배치"
          value={shopEntityData?.productDetailLayout ?? "HORIZONTAL"}
          options={[
            { value: "HORIZONTAL", label: (<><LayoutIcon vertical={false} />가로형</>) },
            { value: "VERTICAL", label: (<><LayoutIcon vertical />세로형</>) },
          ]}
          onChange={(productDetailLayout) => update({ productDetailLayout })}
        />
      </SettingsCard>

      <SettingsCard
        title="나의 디자인 (셀프 디자인)"
        description="끄면 마이페이지의 나의 디자인 메뉴와 헤더의 내 디자인 아이콘이 사라지고, 주문 화면에서 [다음단계]를 누르면 디자인 방법 선택 없이 파일 업로드 창이 바로 열립니다."
        toggle={{
          checked: shopEntityData?.myDesignEnabled ?? true,
          onChange: (myDesignEnabled) => update({ myDesignEnabled }),
          label: "나의 디자인 사용",
        }}
      />

      <SettingsCard
        title="실시간 상담"
        description="쇼핑몰 오른쪽 아래 상담 버튼과 헤더의 1:1 실시간 문의 메뉴입니다."
        toggle={{
          checked: shopEntityData?.consultEnabled ?? false,
          onChange: (consultEnabled) => update({ consultEnabled }),
          label: "실시간 상담 사용",
        }}
      >
        {liveConsult === undefined ? null : liveConsult ? (
          <div className="text-[13px] text-[#555]">
            연동된 채널: <span className="font-bold text-[#252525]">{CHANNEL_NAME[liveConsult.consultChannelType]}</span>
            {liveConsult.consultChannelType === "KAKAO_CHANNEL" && liveConsult.kakaoChannelUrl ? (
              <span className="break-all text-[#888]"> ({liveConsult.kakaoChannelUrl})</span>
            ) : null}
            <div className="mt-[0.25rem] text-[12px] text-[#999]">채널 변경은 내 회사 정보 &gt; 추가 서비스 연동에서 합니다.</div>
          </div>
        ) : (
          <Notice tone="warn">
            상담 플러그인이 없어 켜 두어도 쇼핑몰에 상담 버튼이 나타나지 않습니다.{" "}
            {companyId !== null ? (
              <Link to={`/company/${companyId}/info`} className="underline font-medium">
                내 회사 정보 &gt; 추가 서비스 연동
              </Link>
            ) : null}
            에서 먼저 등록해 주세요.
          </Notice>
        )}
      </SettingsCard>
    </>
  );
};
